import Box from '@mui/material/Box';
import type { FC, ReactNode } from 'react';
import CommandBlock from './CommandBlock';
import DeployStepSection from './DeployStepSection';
import {
  getInstallCommandGroupTitle,
  groupInstallCommands,
} from './groupInstallCommands';

type InstallCommandStepsProps = {
  commands: string[];
  /** First step number for the command groups (default 1). */
  startStepNumber?: number;
  copiedMap: Record<number, boolean>;
  onCopy: (groupIndex: number) => void;
  /** Extra content under the first group's title (e.g. PowerShell note). */
  firstGroupHeader?: ReactNode;
  /** Extra content after the first group's command block (e.g. Linux requirements). */
  firstGroupFooter?: ReactNode;
};

const InstallCommandSteps: FC<InstallCommandStepsProps> = ({
  commands,
  startStepNumber = 1,
  copiedMap,
  onCopy,
  firstGroupHeader,
  firstGroupFooter,
}) => {
  const groups = groupInstallCommands(commands);

  return (
    <>
      {groups.map((command, idx) => {
        const isLast = idx === groups.length - 1;
        const stepNumber = startStepNumber + idx;

        return (
          <DeployStepSection
            key={`install-cmd-group-${idx}`}
            stepNumber={stepNumber}
            title={getInstallCommandGroupTitle(idx, groups.length)}
            complete={!!copiedMap[idx]}
            dimmed={idx > 0 && !copiedMap[idx - 1]}
            isLast={isLast}
          >
            {idx === 0 && firstGroupHeader ? (
              <Box style={{ marginBottom: 4 }}>{firstGroupHeader}</Box>
            ) : null}
            <CommandBlock
              command={command}
              multiline
              onCopy={() => onCopy(idx)}
            />
            {idx === 0 && firstGroupFooter ? (
              <Box style={{ marginTop: 12 }}>{firstGroupFooter}</Box>
            ) : null}
          </DeployStepSection>
        );
      })}
    </>
  );
};

export default InstallCommandSteps;

/** Re-export helper for callers that only need the grouped strings. */
export { groupInstallCommands } from './groupInstallCommands';
